/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Grid, TextField, Typography, Radio, RadioGroup, FormControlLabel, Autocomplete } from '@mui/material';
import { Button, Popconfirm } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { fetchWalletBalanceAsync } from 'redux/WalletSlice';
import { handleCatchError } from 'utils/CatchHandler';
import GlobalBackButton from 'views/utilities/BackButtonGlobal';
import BcConvertModal from 'views/Components/BcConvertModal';
import Spinner from 'views/Components/LoadingSinner';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import VerificationModal from './DmtReusableComponents/VerificationModal';

import {
    useGetBankList,
    useCheckMerchantStatus,
    useCreateBeneficiary,
    useVerifyBeneficiaryPennyDrop
} from './DmtHooks/useCreateBeneficiaryHooks';

const commonStyles = { m: 0 };

const CreateBeneficiary = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Data
    const shouldshowEsign = localStorage.getItem('shouldshowEsign');
    const senderno = localStorage.getItem('sendernumber');
    const agentId = localStorage.getItem('id');
    const dmt_type = 1;
    const itemData = location?.state?.item || location?.state?.data;
    const customerId = location?.state?.customerId;

    // Hooks
    const { data: bankList = [] } = useGetBankList();
    const { data: merchantStatus, isLoading: isCheckLoading } = useCheckMerchantStatus(agentId);
    const createMutation = useCreateBeneficiary();
    const verifyPennyDropMutation = useVerifyBeneficiaryPennyDrop();

    const [modalState, setModalState] = useState({ merchantModal: false, verificationModal: false });
    const [isBcConvertPending, setIsBcConvertPending] = useState(false);
    const [details, setDetails] = useState({ accountName: '', accountNo: '', ifsc: '' });

    const initialValues = {
        title: '',
        benefname: '',
        benelname: '',
        AccountNo: '',
        bankname: '',
        ifsc: '',
        MobileNo: ''
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required('Please select a title'),
        benefname: Yup.string()
            .required('First Name is required')
            .max(25, 'Maximum 25 characters allowed')
            .matches(/^[a-zA-Z\s]*$/, 'Only alphabets are allowed'),
        benelname: Yup.string()
            .required('Last Name is required')
            .max(25, 'Maximum 25 characters allowed')
            .matches(/^[a-zA-Z\s]*$/, 'Only alphabets are allowed'),
        AccountNo: Yup.string()
            .required('Account Number is required')
            .matches(/^\d{1,18}$/, 'Enter a valid Account Number'),
        bankname: Yup.string(),
        ifsc: Yup.string()
            .required('IFSC Code is required')
            .matches(/^[A-Z]{4}[0][A-Z0-9]{6}$/, 'Enter a valid IFSC Code'),
        MobileNo: Yup.string()
            .required('Mobile Number is required')
            .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number starting with 6-9')
    });

    useEffect(() => {
        if (merchantStatus) {
            setIsBcConvertPending(merchantStatus.isPending);
            if (merchantStatus.agentType === 'Merchant' || merchantStatus.isPending || shouldshowEsign === 'true') {
                setModalState((prev) => ({ ...prev, merchantModal: true }));
            }
        }
    }, [merchantStatus, shouldshowEsign]);

    const handleCreateBeneficiary = (values) => {
        const payload = {
            agentId: 2,
            senderno,
            mobileno: values.MobileNo,
            accountno: values.AccountNo,
            bankname: values.bankname,
            ifsccode: values.ifsc,
            title: values.title,
            dmtType: dmt_type,
            benefname: values.benefname,
            benelname: values.benelname,
            customerId
        };
        createMutation.mutate(payload, {
            onSuccess: (response) => {
                const { success, message, data } = response?.data || {};
                const isDuplicate = response?.data?.data?.isDuplicate;
                if (success) {
                    globalNotifySuccess(message || 'Beneficiary added succesfully');
                    if (modalState.verificationModal) setModalState((prev) => ({ ...prev, verificationModal: false }));
                    navigate('/moneyTransfer1/viewBeneficiary', { state: { success, data, senderno, created: 1, customerId } });
                } else {
                    globalNotifyError(message || (isDuplicate ? 'The beneficiary already exists!' : 'Failed to add beneficiary'));
                }
            },
            onError: (error) => handleCatchError(error)
        });
    };

    const handlePennyDrop = (values) => {
        const payload = {
            agentid: agentId,
            name: values.benefname + ' ' + values.benelname,
            customerId: itemData?.id || itemData?.data?.[0]?.id,
            accountno: values.AccountNo,
            ifsccode: values.ifsc,
            dmt_type
        };
        verifyPennyDropMutation.mutate(payload, {
            onSuccess: (response) => {
                if (response?.data?.success) {
                    dispatch(fetchWalletBalanceAsync());
                    globalNotifySuccess(response?.data?.message);
                    const vData = response?.data?.data;
                    setDetails({ accountName: vData?.beneAccount, accountNo: vData?.beneName, ifsc: vData?.beneIFSC });
                    setModalState((prev) => ({ ...prev, verificationModal: true }));
                } else {
                    globalNotifyError(response?.data?.message || 'Penny Drop Failed');
                }
            },
            onError: (error) => handleCatchError(error)
        });
    };

    const headerFields = useMemo(
        () => [
            { name: 'benefname', label: 'First Name', regex: /^[a-zA-Z\s]*$/ },
            { name: 'benelname', label: 'Last Name', regex: /^[a-zA-Z\s]*$/ },
            { name: 'AccountNo', label: 'Account Number', regex: /^\d*$/ }
        ],
        []
    );

    const footerFields = useMemo(
        () => [
            { name: 'ifsc', label: 'IFSC Code', transform: (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, '') },
            { name: 'MobileNo', label: 'Mobile Number', regex: /^\d*$/ }
        ],
        []
    );

    return (
        <>
            <ToastContainer />
            <Box
                sx={{
                    width: '100%',
                    height: 'auto',
                    backgroundColor: '#fff',
                    ...commonStyles,
                    borderRadius: 2,
                    mb: 2,
                    p: { xs: '20px 10px', sm: 2 }
                }}
            >
                <Typography variant="h3">
                    <GlobalBackButton /> Domestic Money Transfer
                </Typography>
                <Box sx={{ mt: '10px' }}>
                    <Link to="/merchant-home" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <HomeOutlined />
                    </Link>
                    <Typography component="span" style={{ mx: '10px' }}>
                        &gt;
                    </Typography>
                    <Link
                        to="/moneyTransfer1/viewCustomers"
                        state={{ data: itemData }}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        <Typography component="span">View Customer</Typography>
                    </Link>
                    <Typography component="span" style={{ mx: '10px' }}>
                        &gt;
                    </Typography>
                    <Typography component="span">Add Beneficiary</Typography>
                </Box>
            </Box>

            <Box sx={{ backgroundColor: '#fff', ...commonStyles, borderRadius: 2, padding: 2, mb: 1 }}>
                <h2>Add Beneficiary</h2>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleCreateBeneficiary}>
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                        <Form>
                            <RadioGroup row name="title" value={values.title} onChange={handleChange} sx={{ mt: 2, ml: 1 }}>
                                {['Mrs', 'Mr', 'Ms'].map((title) => (
                                    <FormControlLabel key={title} value={title} control={<Radio />} label={`${title}.`} />
                                ))}
                            </RadioGroup>
                            {touched.title && errors.title && (
                                <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                                    {errors.title}
                                </Typography>
                            )}

                            <Box sx={{ flexGrow: 1, mt: 2 }}>
                                <Grid container spacing={2}>
                                    {headerFields.map(({ name, label, regex }) => (
                                        <Grid item xs={12} sm={6} key={name}>
                                            <TextField
                                                label={label}
                                                variant="outlined"
                                                fullWidth
                                                name={name}
                                                value={values[name]}
                                                onChange={(e) => (!regex || regex.test(e.target.value)) && handleChange(e)}
                                                onBlur={handleBlur}
                                                error={touched[name] && Boolean(errors[name])}
                                                helperText={touched[name] && errors[name]}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            freeSolo
                                            options={bankList.map((item) => item.bank_name)}
                                            onChange={(e, v) => {
                                                const bank = bankList.find((b) => b.bank_name === v);
                                                setFieldValue('bankname', v || bank?.bank_name);
                                                if (bank) setFieldValue('ifsc', bank.ifsc);
                                            }}
                                            onInputChange={(e, v) => setFieldValue('bankname', v)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Bank Name" variant="outlined" fullWidth name="bankname" />
                                            )}
                                            value={values.bankname}
                                        />
                                    </Grid>
                                    {footerFields.map(({ name, label, regex, transform }) => (
                                        <Grid item xs={12} sm={6} key={name}>
                                            <TextField
                                                label={label}
                                                variant="outlined"
                                                fullWidth
                                                name={name}
                                                value={values[name]}
                                                onChange={(e) => {
                                                    if (transform) setFieldValue(name, transform(e.target.value));
                                                    else if (!regex || regex.test(e.target.value)) handleChange(e);
                                                }}
                                                onBlur={handleBlur}
                                                error={touched[name] && Boolean(errors[name])}
                                                helperText={touched[name] && errors[name]}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} sm={6} lg={6}></Grid>
                                    {[
                                        { label: 'Proceed Without Penny Drop', action: 'submit', loading: createMutation.isPending },
                                        { label: 'Verify With Penny Drop', action: 'penny', loading: verifyPennyDropMutation.isPending }
                                    ].map((btn, i) => (
                                        <Grid item xs={12} sm={6} lg={3} key={i}>
                                            {btn.action === 'penny' ? (
                                                <Popconfirm
                                                    title="Verify account by dropping a penny into it !"
                                                    description="Using Penny Drop may cost you ₹4."
                                                    onConfirm={() => handlePennyDrop(values)}
                                                    okText="Proceed"
                                                    cancelText="Cancel"
                                                >
                                                    <Button size="middle" type="primary" loading={btn.loading} fullWidth sx={{ mt: 3 }}>
                                                        {btn.label}
                                                    </Button>
                                                </Popconfirm>
                                            ) : (
                                                <Button
                                                    type="primary"
                                                    size="middle"
                                                    loading={btn.loading}
                                                    fullWidth
                                                    htmlType="submit"
                                                    sx={{ mt: 3 }}
                                                    className="MuiButton-contained"
                                                >
                                                    {btn.label}
                                                </Button>
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>

            {modalState.merchantModal && (
                <BcConvertModal
                    isOpen={modalState.merchantModal}
                    handleclose={() => setModalState((p) => ({ ...p, merchantModal: false }))}
                    isPending={isBcConvertPending}
                    serviceName={'dmt'}
                    shouldshowEsign={shouldshowEsign}
                />
            )}
            <Spinner loading={isCheckLoading || verifyPennyDropMutation.isPending} />
            <VerificationModal
                open={modalState.verificationModal}
                onClose={() => setModalState((p) => ({ ...p, verificationModal: false }))}
                onConfirm={handleCreateBeneficiary}
                details={details}
                loading={createMutation.isPending}
            />
        </>
    );
};

export default CreateBeneficiary;
